import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import {
    GetPaymentRequisitionsApiArg,
    PaymentRequisitionWithRelations,
    useLazyGetPaymentRequisitionsQuery,
} from '../../../redux/vmsApi';
import { exportPaymentRequisitionsToExcel } from '../services/paymentRequisitionExcel';

const EXPORT_LIMIT = 100000;

interface PaymentRequisitionsExportButtonProps {
    /** Currently applied filters; paging and email-report options are overridden. */
    apiParams: GetPaymentRequisitionsApiArg;
    reportLabel: string;
    fileNamePrefix: string;
}

const PaymentRequisitionsExportButton: React.FC<PaymentRequisitionsExportButtonProps> = ({
    apiParams,
    reportLabel,
    fileNamePrefix,
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [fetchPaymentRequisitions] = useLazyGetPaymentRequisitionsQuery();

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const exportParams = {
                ...apiParams,
                reportByEmail: false,
                reportName: '',
                skip: 0,
                limit: EXPORT_LIMIT,
            };
            const data = (await fetchPaymentRequisitions(exportParams).unwrap()) as PaymentRequisitionWithRelations[];

            if (!data || data.length === 0) {
                toast.info('No payment requisitions to export for the current filters.');
                return;
            }

            exportPaymentRequisitionsToExcel({
                rows: data,
                filters: apiParams as Record<string, unknown>,
                reportLabel,
                fileNamePrefix,
            });

            if (data.length >= EXPORT_LIMIT) {
                toast.warning(`Export reached the ${EXPORT_LIMIT.toLocaleString()} row limit; narrow the filters to get all rows.`);
            } else {
                toast.success(`Exported ${data.length.toLocaleString()} payment requisition(s).`);
            }
        } catch (error) {
            console.error('Failed to export payment requisitions', error);
            toast.error('Failed to export payment requisitions to Excel.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleExport}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
        >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
    );
};

export default PaymentRequisitionsExportButton;
